import {Injector, NgModule} from '@angular/core';
import {APOLLO_OPTIONS, ApolloModule} from 'apollo-angular';
import {ApolloClientOptions, ApolloLink, InMemoryCache, Observable} from '@apollo/client/core';
import {HttpLink} from 'apollo-angular/http';
import {environment} from '../environments/environment';
import {onError} from '@apollo/client/link/error';
import {setContext} from '@apollo/client/link/context';
import {NotificationService} from './services/notification.service';
import {WebSocketLink} from '@apollo/link-ws';
import {Router} from '@angular/router';
import {StorageService} from './services/storage-service.service';
import {AuthService} from './services/auth.service';

const uri = environment.SERVER_URL + '/graphql';

/** Wraps a Promise so it can be returned from an Apollo error handler to retry the operation. */
function fromPromise(promise: Promise<any>): Observable<any> {
  return new Observable((subscriber) => {
    promise.then(
      (value) => { subscriber.next(value); subscriber.complete(); },
      (err) => subscriber.error(err)
    );
  });
}

export function errorLink(
  notificationService: NotificationService,
  router: Router,
  storageService: StorageService,
  injector: Injector
){

  // AuthService is resolved lazily (not constructor-injected) — AuthService
  // depends on GraphqlService which depends on Apollo, which is built by
  // THIS module's factory, so injecting it directly here would be a
  // circular dependency. By the time a GraphQL error actually happens,
  // Apollo already exists, so grabbing it from the injector on demand is safe.
  const getAuthService = () => injector.get(AuthService);

  // Same problem as the REST interceptor's mutex: several GraphQL calls can
  // hit "Authentication required" in the same tick once the access token
  // expires — share one refresh instead of firing one per operation.
  let isRefreshing = false;
  let pendingRefresh: Promise<string> | null = null;

  const forceLogout = () => {
    notificationService.errorMessage('Session expired or access denied. Please login again.');
    storageService.clear();
    localStorage.clear();
    sessionStorage.clear();
    router.navigateByUrl('/login');
  };

  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const { message } of graphQLErrors) {
        const isAuthError = message.includes('Access is denied') || message.includes('Authentication required');

        if (isAuthError) {
          const alreadyRetried = operation.getContext().refreshedOnce;
          const refreshToken = storageService.get('refreshToken');

          if (!alreadyRetried && refreshToken) {
            if (!isRefreshing) {
              isRefreshing = true;
              pendingRefresh = getAuthService().refreshAccessToken().finally(() => {
                isRefreshing = false;
              });
            }

            // .catch() runs on the native Promise (not Apollo's Observable,
            // which has no .catch()) so a failed refresh logs the user out
            // and lets the rejection propagate through fromPromise instead
            // of ever calling forward(operation) again.
            const retried = pendingRefresh!
              .then((newToken: string) => {
                operation.setContext(({ headers = {} }: any) => ({
                  headers: { ...headers, Authorization: `Bearer ${newToken}` },
                  refreshedOnce: true,
                }));
              })
              .catch((err) => {
                forceLogout();
                throw err;
              });

            return fromPromise(retried).flatMap(() => forward(operation));
          }

          forceLogout();
        } else if (message.toLowerCase().includes('subscription')) {
          notificationService.errorMessage(message);
          router.navigateByUrl('/billing');
        } else {
          notificationService.errorMessage('Error: ' + message);
        }
      }
    }

    if (networkError) {
      if (environment.production) {
        console.log(`[Network error]: message: ${networkError.message}, name: ${networkError.name}, stack:  ${networkError.stack}`);
      } else {
        console.log(`[Network error]: message: ${networkError.message}, name: ${networkError.name}, stack:  ${networkError.stack}`);
      }
    }

    return undefined;
  });
}

const watchQueryContext = setContext((graphQlRequest, { headers }) => {
  if (graphQlRequest.variables.notification) {
    return {
      headers: {
        ...headers,
        // Notification: 'Notification'
      }
    };
  }
});

export function createApollo(httpLink: HttpLink, notificationService: NotificationService, router: Router, storageService: StorageService, injector: Injector): ApolloClientOptions<any> {
  // const wsLink = new WebSocketLink({
  //   uri: environment.SERVER_WS_URL,
  //   options: {
  //     reconnect: true,
  //   },
  // });

  // const splitLink = split(
    // ({ query }) => {
    //   const definition = getMainDefinition(query);
    //   return (definition.kind === 'OperationDefinition' && definition.operation === 'subscription');
    // },
    // wsLink,
    // httpLink.create({ uri })
  // );

  return {
    link: ApolloLink.from([
      errorLink(notificationService, router, storageService, injector),
      watchQueryContext.concat(httpLink.create({ uri })),
      // wsLink
    ]),
    cache: new InMemoryCache({
      addTypename: false,
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'network-only',
      }
    }
  };
}

@NgModule({
  imports: [ApolloModule],
  exports: [ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, NotificationService, Router, StorageService, Injector],
    },
  ],
})
export class GraphQLModule {
}
